import { Injectable } from '@angular/core';
import { tap } from 'rxjs';
import { Invoice, InvoiceConfig, ValidationReport, VatExemption } from '@foundation/invoices/models';
import { GenericRepository } from '@foundation/table/state';

/**
 * Reads go through the CRUD router, which has no create, update or delete route: an invoice is
 * written by the admin endpoints and never by the customer it is addressed to. So there is no
 * `createNew…$()` — the copier template generates one (a name prompt and a POST) and a legal
 * document is not something you conjure by typing a title.
 *
 * Drafting one is a different act, and it lives here rather than in a screen: what the admin
 * endpoint expects — which fields travel, and that the mentions art. L441-9 requires travel with
 * them — follows from `InvoiceConfig`, not from whichever page happens to be open.
 */
@Injectable({ providedIn: 'root' })
export class InvoicesRepository extends GenericRepository<Invoice> {
	/** The resource's own url with `admin/` on it: these are this resource's routes, mounted by
	 * its own api.py — the admin check inside them is the only thing that sets them apart. */
	private _adminUrl = `${this.api_url}/admin`;

	constructor() {
		super('invoice');
	}

	/** What a new invoice would be numbered. Allocates nothing — it is a prediction. */
	nextInvoiceNumber$() {
		return this._requestService.getBasic$<{ number: string }>(`${this._adminUrl}/next-number`);
	}

	/** The exemption mentions a zero-rated line may carry. Legal wordings, so the server owns them
	 * for the same reason it owns the payment terms. */
	vatExemptions$() {
		return this._requestService.getBasic$<VatExemption[]>(`${this._adminUrl}/vat-exemptions`);
	}

	/** A new invoice as the server proposes it: us as the seller, today's date, and the payment
	 * mentions in the wording this app is configured with. A proposal, not a decision — edit it and
	 * post it back. */
	newInvoiceDraft$(userId?: string | null) {
		// With a customer, the buyer comes back filled from the billing details they saved on their
		// own profile: they know their address and their country, and an admin retyping either is
		// an admin getting one of them wrong.
		const forUser = userId ? `?user_id=${userId}` : '';
		return this._requestService.getBasic$<InvoiceConfig>(`${this._adminUrl}/draft-defaults${forUser}`);
	}

	/** Creates the document. It goes over as a partial of the resource — camelCase, which the
	 * model's aliases read as the fields they name, so nothing converts anything on the way. The
	 * server fills in what is its own (number, seller, totals) and answers with the resource, which
	 * goes straight into the store.
	 */
	createInvoice$(partialInvoice: Partial<Invoice>) {
		return this._requestService.post$<Invoice, Partial<Invoice>>(`${this._adminUrl}/drafts`, partialInvoice).pipe(tap((response) => this._keepInvoice(response.result)));
	}

	/** Patches a draft with a partial of the resource — the CRUD patch route's shape, behind the
	 * admin check that is the only reason this endpoint exists. */
	patchInvoice$(invoiceId: string, partialInvoice: Partial<Invoice>) {
		return this._requestService
			.post$<Invoice, Partial<Invoice>>(`${this._adminUrl}/drafts/${invoiceId}/update`, partialInvoice)
			.pipe(tap((response) => this._keepInvoice(response.result)));
	}

	/** Takes the number, renders, freezes, shares. The only irreversible call in the section, and
	 * it answers with the document it froze — which is what the store then holds. */
	issueInvoice$(invoiceId: string) {
		return this._requestService
			.post$<Invoice, Record<string, never>>(`${this._adminUrl}/${invoiceId}/issue`, {})
			.pipe(tap((response) => this._keepInvoice(response.result)));
	}

	/** Deletes a draft. An issued document is refused by the endpoint, not by this. */
	deleteInvoice$(invoiceId: string) {
		return this._requestService
			.post$<{ deleted: boolean }, Record<string, never>>(`${this._adminUrl}/drafts/${invoiceId}/delete`, {})
			.pipe(tap((response) => {
				if (response.result?.deleted) this.store.removeObjectLocally(invoiceId, true);
			}));
	}

	/** The document as it would print. A draft comes back watermarked; nothing is stored, so the
	 * store has nothing to hear about. */
	previewInvoicePdf$(partialInvoice: Partial<Invoice>) {
		return this._requestService.postBlob$(`${this._adminUrl}/preview`, partialInvoice);
	}

	/** Checks the invoice. Our own rules always; the outside engine only when asked, because that
	 * call leaves the building and spends a credit. */
	checkInvoice$(partialInvoice: Partial<Invoice>, { external = false }: { external?: boolean } = {}) {
		// The document travels in the body, saved or not: what the admin wants checked is what is on
		// their screen, and the copy in the database is a different document while they type.
		return this._requestService.post$<ValidationReport, Partial<Invoice>>(`${this._adminUrl}/check?external=${external}`, partialInvoice, {
			silentError: true,
		});
	}

	/** A local store write: it issues no request, so it is fine on this read-only CRUD router. */
	private _keepInvoice(invoice: Invoice | null | undefined) {
		if (invoice) this.store.upsertObjectLocally(invoice, true);
	}
}
